import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import "../CSS/Ads.css";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

interface Ad {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
  createdAt: number;
  location: "Lebanon" | "Abidjan";
  type: "ad";
}

const LebAds: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);

  const { user } = useAuth();

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      axios
        .get(process.env.REACT_APP_API + "/ad-spaces/by-location/Lebanon")
        .then((response) => {
          setAds(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    } catch (error) {
      console.error("Error fetching ads:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (e: React.MouseEvent, adId: string) => {
    e.stopPropagation();
    setSelectedAdId(adId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedAdId) return;

    try {
      await axios.delete(
        process.env.REACT_APP_API + "/ad-spaces/" + selectedAdId
      );
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Ad deleted successfully",
        life: 3000,
      });
      // Refresh the ads list
      fetchAds();
    } catch (error) {
      console.error("Error deleting ad:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete ad",
        life: 3000,
      });
    } finally {
      setShowDeleteDialog(false);
      setSelectedAdId(null);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="ads-container">
      <Toast ref={toast} />
      <ConfirmDialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        message="Are you sure you want to delete this ad?"
        header="Confirm Delete"
        icon="pi pi-exclamation-triangle"
        accept={handleDelete}
        reject={() => setShowDeleteDialog(false)}
      />
      <div className="ads-header">
        <h1>Lebanon Advertisements</h1>
      </div>

      <div className="ads-grid">
        {ads.map((ad) => (
          <div
            key={ad._id}
            className="ad-card-wrapper"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/ads/${ad._id}`);
            }}
          >
            <Card className="ad-card">
              {ad.imageUrl && (
                <div className="ad-image-container">
                  <img src={ad.imageUrl} alt={ad.title} className="ad-image" />
                </div>
              )}
              <div className="ad-content">
                <h2 className="ad-title">{ad.title}</h2>

                {user && (
                  <div className="ad-actions">
                    <Button
                      icon="pi pi-pencil"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/edit-ads/${ad._id}`);
                      }}
                      className="p-button-rounded p-button-success p-mr-2"
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-danger"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        confirmDelete(e, ad._id);
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LebAds;
